import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sparkles, Wand2, Trash2, X, Save, Send, Loader2, Image as ImageIcon, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { dealerListings, buildImageUrl } from '../../services/dealerApi';
import { toast } from '../../hooks/use-toast';
import VehicleIntelligencePanel from '../../components/dealers/VehicleIntelligencePanel';

const inputCls = 'bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus-visible:ring-0';
const labelCls = 'block text-xs uppercase tracking-wider text-slate-400 mb-2';

const Approve = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [enhancing, setEnhancing] = useState(null);

  // editable fields
  const [details, setDetails] = useState({ make: '', model: '', variant: '', transmission: '', body_type: '' });
  const [pricing, setPricing] = useState({ year: '', fuel_type: '', km_driven: '', ownership: '', price: '', condition_summary: '' });
  const [description, setDescription] = useState('');
  const [damages, setDamages] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [intelligence, setIntelligence] = useState(null);

  const [savingCover, setSavingCover] = useState(false);

  const handleSelectCover = async (url) => {
    const updated = photos.map((x) => ({ ...x, is_cover: x.url === url }));
    setPhotos(updated);
    setSavingCover(true);
    try {
      await dealerListings.approve(listingId, { photos: updated });
      toast({ title: 'Cover photo saved' });
    } catch {
      toast({ title: 'Failed to save cover', variant: 'destructive' });
    } finally {
      setSavingCover(false);
    }
  };

  useEffect(() => { load(); }, [listingId]);

  const load = async () => {
    try {
      const res = await dealerListings.get(listingId);
      const l = res.listing;
      setListing(l);
      setDetails({
        make: l.ai_details?.make || '',
        model: l.ai_details?.model || '',
        variant: l.ai_details?.variant || '',
        transmission: l.ai_details?.transmission || '',
        body_type: l.ai_details?.body_type || '',
      });
      setPricing({
        year: l.year ?? '',
        fuel_type: l.fuel_type || '',
        km_driven: l.km_driven ?? '',
        ownership: l.ownership ?? '',
        price: l.price ?? '',
        condition_summary: l.condition_summary || '',
      });
      setDescription(l.description || '');
      setDamages(l.damages || []);
      setPhotos(l.photos || []);
      setIntelligence(l.intelligence || null);
    } catch {
      toast({ title: 'Failed to load', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await dealerListings.approve(listingId, {
        make: details.make,
        model: details.model,
        variant: details.variant || null,
        transmission: details.transmission || null,
        body_type: details.body_type || null,
        year: pricing.year === '' ? null : parseInt(pricing.year),
        fuel_type: pricing.fuel_type || null,
        km_driven: pricing.km_driven === '' ? null : parseInt(pricing.km_driven),
        ownership: pricing.ownership === '' ? null : parseInt(pricing.ownership),
        price: pricing.price === '' ? null : parseFloat(pricing.price),
        condition_summary: pricing.condition_summary || null,
        description,
        damages,
        photos,
      });
      toast({ title: 'Saved' });
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    await saveAll();
    setSaving(true);
    try {
      await dealerListings.publish(listingId);
      toast({ title: 'Listing published!', description: 'It is now live on your dealer site.' });
      navigate('/dealers/inventory');
    } catch (err) {
      toast({ title: 'Publish failed', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      // First save edits to make/model so regen uses them
      await dealerListings.approve(listingId, { make: details.make, model: details.model, variant: details.variant || null });
      const res = await dealerListings.regenerateDescription(listingId);
      setDescription(res.description);
      toast({ title: 'Description regenerated' });
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setRegenerating(false);
    }
  };

  const handleEnhance = async (photoUrl) => {
    setEnhancing(photoUrl);
    try {
      const res = await dealerListings.enhancePhoto(listingId, photoUrl);
      setPhotos((prev) => prev.map((p) => p.url === photoUrl ? { ...p, enhanced_url: res.enhanced_url, is_enhanced: true } : p));
      toast({ title: 'Photo enhanced', description: 'Background swapped to neutral studio gray' });
    } catch (err) {
      toast({ title: 'Enhance failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setEnhancing(null);
    }
  };

  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!listing) return <div className="text-slate-400">Listing not found</div>;

  return (
    <div data-testid="approve-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">Approval</span>
          <h1 className="mt-1 text-3xl font-extrabold text-white tracking-tight flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-cyan-400" /> Review AI Output
          </h1>
          <p className="text-slate-400 text-sm mt-1">Edit anything that's wrong, then publish.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={saveAll} disabled={saving} variant="outline" className="border-slate-700 bg-slate-900/40 text-slate-200" data-testid="approve-save-btn">
            <Save className="w-4 h-4 mr-2" /> Save Draft
          </Button>
          <Button onClick={handlePublish} disabled={saving} data-testid="approve-publish-btn"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-slate-950 font-bold rounded-xl">
            <Send className="w-4 h-4 mr-2" /> Publish
          </Button>
        </div>
      </div>

      {/* Vehicle Details */}
      <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 space-y-4">
        <h2 className="text-base font-semibold text-white">AI Detected Vehicle</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Make *</label>
            <Input data-testid="approve-make" value={details.make} onChange={(e) => setDetails({ ...details, make: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Model *</label>
            <Input data-testid="approve-model" value={details.model} onChange={(e) => setDetails({ ...details, model: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Variant</label>
            <Input data-testid="approve-variant" value={details.variant} onChange={(e) => setDetails({ ...details, variant: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Transmission</label>
            <select
              data-testid="approve-transmission"
              value={details.transmission}
              onChange={(e) => setDetails({ ...details, transmission: e.target.value })}
              className={`${inputCls} h-10 px-3 w-full rounded-md`}
            >
              <option value="">Select</option>
              <option value="Manual">Manual</option>
              <option value="Automatic">Automatic</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Body Type</label>
            <select
              data-testid="approve-body-type"
              value={details.body_type}
              onChange={(e) => setDetails({ ...details, body_type: e.target.value })}
              className={`${inputCls} h-10 px-3 w-full rounded-md`}
            >
              <option value="">Select</option>
              <option value="Hatchback">Hatchback</option>
              <option value="Sedan">Sedan</option>
              <option value="SUV">SUV</option>
              <option value="MUV">MUV</option>
              <option value="Coupe">Coupe</option>
              <option value="Luxury">Luxury</option>
            </select>
          </div>
        </div>
        <div className="text-xs text-slate-500">
          AI confidence: <span className="text-cyan-400">{Math.round((listing.ai_details?.confidence || 0) * 100)}%</span>
          {listing.ai_details?.detected_color && <span className="ml-3">Color: <span className="text-slate-300">{listing.ai_details.detected_color}</span></span>}
          {listing.ai_details?.odometer != null && <span className="ml-3">Detected ODO: <span className="text-slate-300">{listing.ai_details.odometer.toLocaleString('en-IN')} km</span></span>}
        </div>
      </div>

      {/* Pricing & Specs */}
      <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 space-y-4" data-testid="approve-pricing-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-base font-semibold text-white">Pricing & Specs</h2>
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono">
            Buyers see these on the listing card
          </span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Price (₹) *</label>
            <Input
              data-testid="approve-price"
              type="number"
              min="0"
              step="1000"
              value={pricing.price}
              onChange={(e) => setPricing({ ...pricing, price: e.target.value })}
              placeholder="e.g. 650000"
              className={inputCls}
            />
            {pricing.price !== '' && !isNaN(parseFloat(pricing.price)) && (
              <p className="mt-1 text-[11px] text-cyan-400 font-mono">
                ₹{parseFloat(pricing.price).toLocaleString('en-IN')}
              </p>
            )}
          </div>
          <div>
            <label className={labelCls}>Year *</label>
            <Input
              data-testid="approve-year"
              type="number"
              min="1980"
              max={new Date().getFullYear() + 1}
              value={pricing.year}
              onChange={(e) => setPricing({ ...pricing, year: e.target.value })}
              placeholder="e.g. 2021"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>KM Driven</label>
            <Input
              data-testid="approve-km"
              type="number"
              min="0"
              step="100"
              value={pricing.km_driven}
              onChange={(e) => setPricing({ ...pricing, km_driven: e.target.value })}
              placeholder="e.g. 34000"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Fuel Type *</label>
            <select
              data-testid="approve-fuel"
              value={pricing.fuel_type}
              onChange={(e) => setPricing({ ...pricing, fuel_type: e.target.value })}
              className={`${inputCls} h-10 px-3 w-full rounded-md`}
            >
              <option value="">Select</option>
              <option value="Petrol">Petrol</option>
              <option value="Diesel">Diesel</option>
              <option value="CNG">CNG</option>
              <option value="Electric">Electric</option>
              <option value="Hybrid">Hybrid</option>
              <option value="LPG">LPG</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Number of Owners</label>
            <select
              data-testid="approve-ownership"
              value={pricing.ownership}
              onChange={(e) => setPricing({ ...pricing, ownership: e.target.value })}
              className={`${inputCls} h-10 px-3 w-full rounded-md`}
            >
              <option value="">Select</option>
              <option value="1">1st owner</option>
              <option value="2">2nd owner</option>
              <option value="3">3rd owner</option>
              <option value="4">4th owner</option>
              <option value="5">5+ owners</option>
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls}>Condition Summary</label>
          <Input
            data-testid="approve-condition"
            value={pricing.condition_summary}
            onChange={(e) => setPricing({ ...pricing, condition_summary: e.target.value })}
            placeholder="e.g. Single owner, well-maintained"
            className={inputCls}
          />
          <p className="mt-1 text-[10px] text-slate-500">
            Short headline shown on the public car page (max 100 chars).
          </p>
        </div>
      </div>

      {/* Description */}
      <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">AI Description</h2>
          <Button onClick={handleRegenerate} disabled={regenerating} size="sm" variant="outline" className="border-slate-700 bg-slate-900/40 text-slate-200" data-testid="approve-regenerate-btn">
            <Wand2 className="w-4 h-4 mr-2" /> {regenerating ? 'Regenerating...' : 'Regenerate'}
          </Button>
        </div>
        <Textarea data-testid="approve-description" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} />
      </div>

      {/* Vehicle Intelligence (specs + features auto-fetched) */}
      <VehicleIntelligencePanel
        listingId={listingId}
        intelligence={intelligence}
        onRefresh={(intel) => setIntelligence(intel)}
      />

      {/* Damages */}
      <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 space-y-3">
        <h2 className="text-base font-semibold text-white">Visible Damages Detected</h2>
        {damages.length === 0 ? (
          <p className="text-sm text-slate-400">No visible damages detected — clean car!</p>
        ) : (
          <div className="space-y-2">
            {damages.map((d, idx) => (
              <div key={d.id || idx} className="flex items-start gap-3 p-3 rounded-xl bg-slate-950/60 border border-slate-800" data-testid={`damage-${idx}`}>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  d.severity === 'major' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
                  d.severity === 'moderate' ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' :
                  'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                }`}>
                  {d.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <Input value={d.location || ''} onChange={(e) => setDamages((prev) => prev.map((x, i) => i === idx ? { ...x, location: e.target.value } : x))} className={`${inputCls} h-8 mb-1.5`} placeholder="Location" />
                  <Input value={d.description || ''} onChange={(e) => setDamages((prev) => prev.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} className={`${inputCls} h-8 text-sm`} placeholder="Description" />
                </div>
                <button onClick={() => setDamages((prev) => prev.filter((_, i) => i !== idx))} className="p-1 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => setDamages([...damages, { id: Date.now().toString(), severity: 'minor', location: '', description: '' }])}
          className="border-slate-700 bg-slate-900/40 text-slate-200"
          data-testid="approve-add-damage-btn"
        >
          + Add damage manually
        </Button>
      </div>

      {/* Photos with enhancement */}
      <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 space-y-3">
        <h2 className="text-base font-semibold text-white">Photos ({photos.length})</h2>
        <p className="text-xs text-slate-400">Pick a cover photo (the one buyers see first), and tap "Enhance" to swap the background to a clean studio gray.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p) => {
            const isCover = !!p.is_cover;
            return (
              <div key={p.url} className={`relative bg-slate-800 rounded-xl overflow-hidden group ring-2 ${isCover ? 'ring-cyan-400' : 'ring-transparent'}`} data-testid={`photo-card-${p.url}`}>
                <div className="aspect-video">
                  <img src={buildImageUrl(p.enhanced_url || p.url)} alt="" className="w-full h-full object-cover" />
                </div>
                {isCover && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-cyan-500 text-slate-950 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1">
                    <Star className="w-3 h-3 fill-current" /> Cover
                  </span>
                )}
                {p.is_enhanced && !isCover && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-cyan-500 text-slate-950 text-[10px] font-bold uppercase tracking-wider">Enhanced</span>
                )}
                <div className="absolute inset-x-2 bottom-2 flex gap-1">
                  {!isCover ? (
                    <Button
                      size="sm"
                      onClick={() => handleSelectCover(p.url)}
                      disabled={savingCover}
                      data-testid="photo-cover-btn"
                      className="flex-1 h-7 text-xs bg-slate-950/80 hover:bg-slate-950 text-cyan-300 border border-cyan-500/40"
                    >
                      <Star className="w-3 h-3 mr-1" /> Cover
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleEnhance(p.url)}
                      disabled={enhancing === p.url}
                      className="flex-1 h-7 text-xs bg-cyan-500 hover:bg-cyan-400 text-slate-950"
                      data-testid="photo-enhance-btn"
                    >
                      {enhancing === p.url ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ImageIcon className="w-3 h-3 mr-1" /> Enhance</>}
                    </Button>
                  )}
                  {!isCover && (
                    <Button
                      size="sm"
                      onClick={() => handleEnhance(p.url)}
                      disabled={enhancing === p.url}
                      className="h-7 text-xs bg-cyan-500 hover:bg-cyan-400 text-slate-950 px-2"
                    >
                      {enhancing === p.url ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                    </Button>
                  )}
                  <button onClick={() => setPhotos((prev) => prev.filter((x) => x.url !== p.url))} className="w-7 h-7 rounded-md bg-slate-950/80 text-white flex items-center justify-center hover:text-red-400">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Approve;
